import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { ReactNode } from 'react'
import { BadgeColor } from './badgeTypes.ts'

const COLOR_STYLES: Record<BadgeColor, string> = {
    [BadgeColor.Success]:
        'bg-badge-success-bg text-badge-success-text border-badge-success-border',
    [BadgeColor.Success2]:
        'bg-badge-success2-bg text-badge-success2-text border-badge-success2-border',
    [BadgeColor.Success3]:
        'bg-badge-success3-bg text-badge-success3-text border-badge-success3-border',
    [BadgeColor.SuccessTeal]:
        'bg-badge-success-teal-bg text-badge-success-teal-text border-badge-success-teal-border',
    [BadgeColor.Neutral]:
        'bg-badge-neutral-bg text-badge-neutral-text border-badge-neutral-border',
    [BadgeColor.Neutral2]:
        'bg-badge-neutral2-bg text-badge-neutral2-text border-badge-neutral2-border',
    [BadgeColor.Warning]:
        'bg-badge-warning-bg text-badge-warning-text border-badge-warning-border',
    [BadgeColor.WarningAmber]:
        'bg-badge-warning-amber-bg text-badge-warning-amber-text border-badge-warning-amber-border',
    [BadgeColor.Progress]:
        'bg-badge-progress-bg text-badge-progress-text border-badge-progress-border',
    [BadgeColor.Hold]:
        'bg-badge-hold-bg text-badge-hold-text border-badge-hold-border',
    [BadgeColor.Error]:
        'bg-badge-error-bg text-badge-error-text border-badge-error-border',
    [BadgeColor.Error2]:
        'bg-badge-error2-bg text-badge-error2-text border-badge-error2-border',
    [BadgeColor.Info]:
        'bg-badge-info-bg text-badge-info-text border-badge-info-border',
    [BadgeColor.Indigo]:
        'bg-badge-indigo-bg text-badge-indigo-text border-badge-indigo-border',
    [BadgeColor.Violet]:
        'bg-badge-violet-bg text-badge-violet-text border-badge-violet-border',
    [BadgeColor.Cyan]:
        'bg-badge-cyan-bg text-badge-cyan-text border-badge-cyan-border',
    [BadgeColor.Pink]:
        'bg-badge-pink-bg text-badge-pink-text border-badge-pink-border',
    [BadgeColor.BrandBlue]:
        'bg-badge-brandBlue-bg text-badge-brandBlue-text border-badge-brandBlue-border',
    [BadgeColor.InfoBlue]:
        'bg-badge-infoBlue-bg text-badge-infoBlue-text border-badge-infoBlue-border',
    [BadgeColor.SkyBlue]:
        'bg-badge-skyBlue-bg text-badge-skyBlue-text border-badge-skyBlue-border',
    [BadgeColor.NavyBlue]:
        'bg-badge-navyBlue-bg text-badge-navyBlue-text border-badge-navyBlue-border',
    [BadgeColor.TopPickYellow]:
        'bg-yellow-300 text-amber-900 border-yellow-300',
}

// Local (not exported) props type to keep this file exporting only a component
type BadgeProps = {
    color?: BadgeColor
    className?: string
    children: ReactNode
    leftIcon?: ReactNode
    rightIcon?: ReactNode
    title?: string
    onClick?: () => void
    borderless?: boolean
}

export default function Badge({
    color = BadgeColor.Neutral,
    className,
    children,
    leftIcon,
    rightIcon,
    title,
    onClick,
    borderless = false,
}: BadgeProps) {
    return (
        <span
            className={twMerge(
                clsx(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium whitespace-nowrap',
                    COLOR_STYLES[color],
                    { 'border-transparent': borderless },
                    className,
                ),
            )}
            title={title}
            role="status"
            onClick={onClick}
        >
            {leftIcon ? (
                <span className="-ml-0.5 inline-flex">{leftIcon}</span>
            ) : null}
            <span className="leading-tight">{children}</span>
            {rightIcon ? (
                <span className="-mr-0.5 inline-flex">{rightIcon}</span>
            ) : null}
        </span>
    )
}
